import React, { useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  getCompanyUploadedFileSignedUrl,
  deleteCompanyUploadedFile,
  deletePaymentPlatformFile,
  getPaymentPlatformFiles,
  previewPaymentPlatformImport,
  registerPaymentPlatformFile,
  startPaymentPlatformImport,
} from "../api/companyApi";
import PaymentPlatformColumnMappingDialog from "./PaymentPlatformColumnMappingDialog";
import PaymentPlatformDataCenter from "./PaymentPlatformDataCenter";
import PaymentPlatformFileUploadDialog from "./PaymentPlatformFileUploadDialog";
import { latestPaymentPlatformFile } from "../utils/latestPaymentPlatformFile";
import { cleanupCancelledPaymentPlatformUpload } from "../utils/cancelledPaymentPlatformUpload";

export default function PaymentPlatformDataCenterWorkspace({
  tenantId,
  title = "Data center",
  description,
  uploadLabel,
  uploadDialogTitle,
  uploadDialogDescription,
  source = "dashboard_upload",
}) {
  const fileInput = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pendingImport, setPendingImport] = useState(null);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const chooseFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length) setSelectedFiles(files);
  };

  const uploadFiles = async (labelledFiles) => {
    setBusy(true);
    const uploaded = [];
    try {
      for (const { file, labels } of labelledFiles) {
        const signed = await getCompanyUploadedFileSignedUrl(tenantId, {
          fileName: file.name,
          fileType: file.type || "application/octet-stream",
        });
        await axios.put(signed.data.uploadUrl, file, {
          headers: { "Content-Type": file.type || "application/octet-stream" },
        });
        const storageFile = {
          fileGCSKey: signed.data.fileGCSKey,
          fileName: signed.data.originalFileName || file.name,
        };
        uploaded.push(storageFile);
        const catalogFile = await registerPaymentPlatformFile(tenantId, {
          ...storageFile,
          labels,
          source,
        });
        uploaded[uploaded.length - 1] = catalogFile;
      }
      // Keep the automatic post-upload run scoped to the files selected in this
      // interaction. Historical files remain visible in the data center but do
      // not silently expand a later mapping run.
      const preview = await previewPaymentPlatformImport(tenantId, uploaded);
      setPendingImport({ files: uploaded, preview });
      setSelectedFiles([]);
      setRefreshKey((value) => value + 1);
      toast.success("Files added. Map columns for this upload.");
    } catch (error) {
      if (uploaded.length) {
        await cleanupCancelledPaymentPlatformUpload({
          tenantId,
          files: uploaded,
          deleteCatalogFile: deletePaymentPlatformFile,
          deleteStorageFile: deleteCompanyUploadedFile,
        });
        setRefreshKey((value) => value + 1);
      }
      toast.error(error?.response?.data?.message || "File upload failed");
    } finally {
      setBusy(false);
    }
  };

  const mapLatestFile = async () => {
    setBusy(true);
    try {
      const catalog = await getPaymentPlatformFiles(tenantId);
      const latestFile = latestPaymentPlatformFile(catalog?.items);
      if (!latestFile) {
        toast.error("Upload at least one file before mapping columns.");
        return;
      }
      const preview = await previewPaymentPlatformImport(tenantId, [latestFile]);
      setMappingPreview({ ...preview, files: [latestFile] });
      setMappingOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not inspect tenant files");
    } finally {
      setBusy(false);
    }
  };

  const applyMapping = async (columnMapping) => {
    const files = pendingImport?.files || mappingPreview?.files;
    if (!files?.length) return;
    setBusy(true);
    try {
      await startPaymentPlatformImport(
        tenantId,
        files,
        null,
        columnMapping,
        Boolean(pendingImport),
      );
      setPendingImport(null);
      setMappingOpen(false);
      toast.success("Mapping saved. Assessments are rebuilding from the latest uploaded file.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not rebuild assessments");
    } finally {
      setBusy(false);
    }
  };

  const cancelPendingImport = async () => {
    if (!pendingImport?.files?.length) {
      setPendingImport(null);
      setMappingOpen(false);
      return;
    }
    setBusy(true);
    try {
      const result = await cleanupCancelledPaymentPlatformUpload({
        tenantId,
        files: pendingImport.files,
        deleteCatalogFile: deletePaymentPlatformFile,
        deleteStorageFile: deleteCompanyUploadedFile,
      });
      if (result.failed.length) {
        setPendingImport((current) => ({ ...current, files: result.failed }));
        toast.error("Cancellation was incomplete. The remaining uploaded files were kept for retry.");
        return;
      }
      setPendingImport(null);
      setRefreshKey((value) => value + 1);
      toast.info("Upload cancelled. Uploaded files and staged data were removed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        hidden
        multiple
        ref={fileInput}
        type="file"
        accept=".csv,.xls,.xlsx"
        onChange={chooseFiles}
      />
      <PaymentPlatformDataCenter
        tenantId={tenantId}
        refreshKey={refreshKey}
        busy={busy}
        title={title}
        description={description}
        uploadLabel={uploadLabel}
        onUpload={() => fileInput.current?.click()}
        onMapColumns={mapLatestFile}
      />
      <PaymentPlatformFileUploadDialog
        open={selectedFiles.length > 0}
        files={selectedFiles}
        busy={busy}
        title={uploadDialogTitle}
        description={uploadDialogDescription}
        onClose={() => setSelectedFiles([])}
        onUpload={uploadFiles}
      />
      <PaymentPlatformColumnMappingDialog
        open={Boolean(pendingImport) || mappingOpen}
        preview={pendingImport?.preview || mappingPreview}
        currentMapping={null}
        busy={busy}
        onClose={() => {
          if (pendingImport) {
            cancelPendingImport();
            return;
          }
          setMappingOpen(false);
        }}
        onApply={applyMapping}
        onSaveDraft={null}
      />
    </>
  );
}
